import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  usageAdminOverview, usageAdminAdjust, usagePlans, usageEditPlan,
  usageAppControls, usageSetAppControls, usageAdminAudit, usageExportCsv,
  usageBillingEmails, usageTestBillingEmail,
} from "@/lib/api";
import { toast } from "sonner";
import { Power, DownloadSimple, Warning, EnvelopeSimple } from "@phosphor-icons/react";

const STATUS_COLORS = {
  trialing: "text-amber-300", active: "text-emerald-400", expired: "text-red-400",
  past_due: "text-red-400", suspended: "text-red-400", canceled: "text-[#9a9a9a]",
};
const BETA_STATUSES = ["applied", "under_review", "selected", "invited", "active", "expiring", "converted", "declined", "expired"];

function AppControls() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["usage-app-controls"], queryFn: usageAppControls, refetchInterval: 30000 });
  const s = q.data || {};
  const [daily, setDaily] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [grace, setGrace] = useState(null);

  const save = async (body) => {
    try {
      await usageSetAppControls(body);
      toast.success("Emergency controls updated");
      qc.invalidateQueries({ queryKey: ["usage-app-controls"] });
      qc.invalidateQueries({ queryKey: ["usage-audit"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className={`rounded-2xl border p-5 ${s.ai_paused ? "border-red-500/50 bg-red-500/10" : "border-white/10 bg-white/[0.03]"}`} data-testid="app-controls-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.ai_paused ? "bg-red-500/20" : "bg-[#FF5E00]/15"}`}>
            <Power size={20} className={s.ai_paused ? "text-red-400" : "text-[#FF5E00]"} />
          </div>
          <div>
            <div className="font-semibold">Emergency AI pause</div>
            <div className="text-xs text-[#9a9a9a]">Instantly stops ALL AI generation platform-wide. Saved content stays available.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {s.ai_paused && <Badge className="bg-red-500 text-black" data-testid="app-paused-badge">AI PAUSED</Badge>}
          <Switch checked={!!s.ai_paused} data-testid="app-pause-switch"
            onCheckedChange={(v) => save({ ai_paused: v, reason: v ? "Emergency pause" : "Resume" })} />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Credits today</div>
          <div className="text-lg font-semibold" data-testid="credits-today">{s.credits_today ?? 0}<span className="text-xs text-[#9a9a9a]"> / {s.daily_ceiling_credits}</span></div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Credits this month</div>
          <div className="text-lg font-semibold" data-testid="credits-month">{s.credits_this_month ?? 0}<span className="text-xs text-[#9a9a9a]"> / {s.monthly_ceiling_credits}</span></div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Daily ceiling</Label>
          <div className="flex gap-2 mt-1">
            <Input type="number" className="h-8" data-testid="daily-ceiling-input"
              value={daily ?? s.daily_ceiling_credits ?? ""} onChange={(e) => setDaily(e.target.value)} />
            <Button size="sm" variant="outline" className="h-8 border-white/15" data-testid="daily-ceiling-save"
              onClick={() => save({ daily_ceiling_credits: Number(daily), reason: "Ceiling update" })} disabled={daily == null}>Set</Button>
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Monthly ceiling</Label>
          <div className="flex gap-2 mt-1">
            <Input type="number" className="h-8" data-testid="monthly-ceiling-input"
              value={monthly ?? s.monthly_ceiling_credits ?? ""} onChange={(e) => setMonthly(e.target.value)} />
            <Button size="sm" variant="outline" className="h-8 border-white/15" data-testid="monthly-ceiling-save"
              onClick={() => save({ monthly_ceiling_credits: Number(monthly), reason: "Ceiling update" })} disabled={monthly == null}>Set</Button>
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Payment grace (days)</Label>
          <div className="flex gap-2 mt-1">
            <Input type="number" className="h-8" data-testid="grace-days-input"
              value={grace ?? s.grace_days ?? 7} onChange={(e) => setGrace(e.target.value)} />
            <Button size="sm" variant="outline" className="h-8 border-white/15" data-testid="grace-days-save"
              onClick={() => save({ grace_days: Number(grace), reason: "Grace period update" })} disabled={grace == null}>Set</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManageDialog({ row, plans, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [extendDays, setExtendDays] = useState("");
  const [bonusFeature, setBonusFeature] = useState("");
  const [bonusAmount, setBonusAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async (body) => {
    setBusy(true);
    try {
      await usageAdminAdjust(row.client_id, { ...body, reason });
      toast.success("Applied");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const featureKeys = Object.keys(row.usage || {});
  const allFeatures = featureKeys.length ? featureKeys : ["images", "videos", "social_posts", "emails", "blogs", "rewrites", "marketing_scores", "monthly_campaigns", "campaign_intelligence", "cmo_planning"];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[#111116] border-white/10 text-white max-h-[85vh] overflow-y-auto" data-testid="manage-workspace-dialog">
        <DialogHeader><DialogTitle>Manage — {row.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label className="text-xs text-[#9a9a9a]">Reason (recorded in the audit trail)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you making this change?" className="mt-1" data-testid="manage-reason-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#9a9a9a]">Change plan</Label>
              <Select onValueChange={(v) => apply({ plan: v })}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="manage-plan-select"><SelectValue placeholder={row.plan_name} /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white">
                  {plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#9a9a9a]">Beta status</Label>
              <Select onValueChange={(v) => apply({ beta_status: v })}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="manage-beta-select"><SelectValue placeholder={row.beta_status || "—"} /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white">
                  {BETA_STATUSES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-white/10 p-3 space-y-2.5">
            <div className="font-medium text-xs uppercase tracking-wider text-[#9a9a9a]">Trial lifecycle</div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="border-white/15" disabled={busy} data-testid="manage-start-trial"
                onClick={() => apply({ start_trial: true })}>Start / restart trial</Button>
              <Button size="sm" variant="outline" className="border-white/15" disabled={busy} data-testid="manage-end-trial"
                onClick={() => apply({ end_trial: true })}>End trial now</Button>
              <div className="flex items-center gap-1.5">
                <Input type="number" placeholder="days" value={extendDays} onChange={(e) => setExtendDays(e.target.value)}
                  className="h-8 w-20" data-testid="manage-extend-days-input" />
                <Button size="sm" variant="outline" className="border-white/15 h-8" disabled={busy || !extendDays} data-testid="manage-extend-trial"
                  onClick={() => apply({ extend_trial_days: Number(extendDays) })}>Extend</Button>
              </div>
            </div>
            <div className="text-xs text-[#9a9a9a]">Trial ends: {row.trial_ends_at ? row.trial_ends_at.slice(0, 10) : "not started"} {row.trial_days_left != null && `(${row.trial_days_left}d left)`}</div>
          </div>

          <div className="rounded-lg border border-white/10 p-3 space-y-2.5">
            <div className="font-medium text-xs uppercase tracking-wider text-[#9a9a9a]">Allowances</div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Select value={bonusFeature} onValueChange={setBonusFeature}>
                <SelectTrigger className="h-8 w-44 bg-white/5 border-white/10" data-testid="manage-bonus-feature"><SelectValue placeholder="Feature" /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white">
                  {allFeatures.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="+amount" value={bonusAmount} onChange={(e) => setBonusAmount(e.target.value)} className="h-8 w-24" data-testid="manage-bonus-amount" />
              <Button size="sm" variant="outline" className="border-white/15 h-8" disabled={busy || !bonusFeature || !bonusAmount} data-testid="manage-grant-bonus"
                onClick={() => apply({ bonus_feature: bonusFeature, bonus_amount: Number(bonusAmount) })}>Grant bonus</Button>
              <Button size="sm" variant="outline" className="border-white/15 h-8" disabled={busy || !bonusFeature} data-testid="manage-reset-feature"
                onClick={() => apply({ reset_feature: bonusFeature })}>Reset counter</Button>
            </div>
          </div>

          <div className="rounded-lg border border-white/10 p-3 flex items-center justify-between">
            <div>
              <div className="font-medium">Suspend AI on this workspace</div>
              <div className="text-xs text-[#9a9a9a]">Blocks all AI generation; data untouched.</div>
            </div>
            <Switch checked={!!row.ai_suspended} data-testid="manage-suspend-switch"
              onCheckedChange={(v) => apply({ ai_suspended: v })} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlansEditor() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["usage-plans"], queryFn: usagePlans });
  const [edit, setEdit] = useState({});
  const plans = q.data || [];

  const save = async (p) => {
    const e = edit[p.plan_id] || {};
    try {
      await usageEditPlan(p.plan_id, {
        limits: e.limits, trial_days: e.trial_days != null ? Number(e.trial_days) : undefined,
        price_monthly: e.price_monthly != null ? Number(e.price_monthly) : undefined,
        reason: "Plan config edit",
      });
      toast.success(`${p.name} updated`);
      setEdit((s) => ({ ...s, [p.plan_id]: undefined }));
      qc.invalidateQueries({ queryKey: ["usage-plans"] });
      qc.invalidateQueries({ queryKey: ["usage-audit"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="plans-editor">
      {plans.map((p) => {
        const e = edit[p.plan_id] || {};
        return (
          <div key={p.plan_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div className="font-semibold">{p.name}</div>
              <Badge variant="outline" className="border-white/15 text-[#9a9a9a]">{p.plan_id}</Badge>
              <div className="flex items-center gap-1.5 text-xs text-[#9a9a9a]">
                Trial days:
                <Input type="number" className="h-7 w-16" value={e.trial_days ?? p.trial_days}
                  onChange={(ev) => setEdit((s) => ({ ...s, [p.plan_id]: { ...e, trial_days: ev.target.value } }))} />
                $/mo:
                <Input type="number" className="h-7 w-20" value={e.price_monthly ?? p.price_monthly}
                  onChange={(ev) => setEdit((s) => ({ ...s, [p.plan_id]: { ...e, price_monthly: ev.target.value } }))} />
              </div>
              <Button size="sm" className="ml-auto bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold h-8"
                data-testid={`plan-save-${p.plan_id}`} onClick={() => save(p)} disabled={!edit[p.plan_id]}>Save</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {Object.entries(p.limits || {}).map(([k, v]) => (
                <div key={k}>
                  <Label className="text-[10px] uppercase tracking-wide text-[#9a9a9a]">{k.replace(/_/g, " ")}</Label>
                  <Input type="number" className="h-8 mt-0.5" data-testid={`plan-${p.plan_id}-limit-${k}`}
                    value={(e.limits && e.limits[k] != null) ? e.limits[k] : v}
                    onChange={(ev) => setEdit((s) => ({ ...s, [p.plan_id]: { ...e, limits: { ...(e.limits || {}), [k]: ev.target.value } } }))} />
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[#9a9a9a] mt-2">-1 = unlimited. Changes apply immediately to every workspace on this plan.</div>
          </div>
        );
      })}
    </div>
  );
}

function BillingEmailsPanel() {
  const q = useQuery({ queryKey: ["usage-billing-emails"], queryFn: usageBillingEmails, refetchInterval: 30000 });
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const d = q.data || {};
  const rows = d.emails || [];

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const r = await usageTestBillingEmail(testTo.trim());
      if (r.ok) toast.success(`Test email sent to ${testTo}`);
      else toast.error(r?.result?.error || "Test email was not delivered — check the log below.");
      q.refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestBusy(false);
    }
  };

  const STATUS_STYLE = {
    sent: "text-emerald-400", failed: "text-red-400", retrying: "text-amber-300",
    skipped_unconfigured: "text-[#9a9a9a]", no_recipient: "text-amber-300", queued: "text-[#9a9a9a]",
  };

  return (
    <div className="space-y-4" data-testid="billing-emails-panel">
      {!d.configured && !q.isLoading && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="email-not-configured-banner">
          <Warning size={20} className="text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-200">
            <div className="font-semibold">Transactional billing emails are not active.</div>
            <div className="mt-1 text-amber-200/80">Add <code className="text-amber-100">RESEND_API_KEY</code> and verify the sending domain to enable delivery. Billing events are still logged below and will send automatically once configured.</div>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <EnvelopeSimple size={18} className={d.configured ? "text-emerald-400" : "text-[#9a9a9a]"} />
          <span className="font-medium">Delivery:</span>
          <Badge variant="outline" className={d.configured ? "border-emerald-500/40 text-emerald-400" : "border-white/15 text-[#9a9a9a]"} data-testid="email-config-badge">
            {d.configured ? `Active${d.sender ? ` — ${d.sender}` : ""}` : "Not configured"}
          </Badge>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Input placeholder="you@vozelto.com" value={testTo} onChange={(e) => setTestTo(e.target.value)}
            className="h-8 w-56" data-testid="test-email-input" />
          <Button size="sm" variant="outline" className="h-8 border-white/15" data-testid="test-email-send"
            disabled={testBusy || !testTo.includes("@") || !d.configured} onClick={sendTest}>
            {testBusy ? "Sending…" : "Send test email"}
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-white/10 overflow-x-auto" data-testid="billing-emails-table">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-[#9a9a9a]">
            <tr><th className="p-3">When</th><th className="p-3">Type</th><th className="p-3">To</th>
              <th className="p-3">Subject</th><th className="p-3">Status</th><th className="p-3">Attempts</th><th className="p-3">Error</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="p-3 text-xs text-[#9a9a9a] whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-xs font-medium">{r.kind}</td>
                <td className="p-3 text-xs">{r.to || "—"}</td>
                <td className="p-3 text-xs max-w-[240px] truncate">{r.subject}</td>
                <td className={`p-3 text-xs font-medium ${STATUS_STYLE[r.status] || ""}`}>{r.status}</td>
                <td className="p-3 text-xs">{r.attempts}</td>
                <td className="p-3 text-xs text-[#9a9a9a] max-w-[200px] truncate">{r.error || ""}</td>
              </tr>
            ))}
            {!rows.length && <tr><td className="p-4 text-sm text-[#9a9a9a]" colSpan={7}>No billing emails yet — they appear here the moment billing events fire.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditTrail() {
  const q = useQuery({ queryKey: ["usage-audit"], queryFn: () => usageAdminAudit() });
  const rows = q.data || [];
  return (
    <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="usage-audit-table">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-[#9a9a9a]">
          <tr><th className="p-3">When</th><th className="p-3">Admin</th><th className="p-3">Workspace</th><th className="p-3">Action</th><th className="p-3">Change</th><th className="p-3">Reason</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-white/5">
              <td className="p-3 text-xs text-[#9a9a9a] whitespace-nowrap">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="p-3 text-xs">{r.admin}</td>
              <td className="p-3 text-xs">{r.client_id}</td>
              <td className="p-3 text-xs font-medium">{r.action}</td>
              <td className="p-3 text-xs text-[#9a9a9a] max-w-[240px] truncate">{JSON.stringify(r.previous)} → {JSON.stringify(r.new)}</td>
              <td className="p-3 text-xs text-[#9a9a9a]">{r.reason}</td>
            </tr>
          ))}
          {!rows.length && <tr><td className="p-4 text-sm text-[#9a9a9a]" colSpan={6}>No admin actions recorded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function UsageControlPage() {
  const qc = useQueryClient();
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [manage, setManage] = useState(null);

  const params = {};
  if (planFilter !== "all") params.plan = planFilter;
  if (statusFilter !== "all") params.status = statusFilter;
  if (search) params.q = search;

  const overviewQ = useQuery({
    queryKey: ["usage-admin-overview", planFilter, statusFilter, search],
    queryFn: () => usageAdminOverview(params),
    refetchInterval: 60000,
  });
  const plansQ = useQuery({ queryKey: ["usage-plans"], queryFn: usagePlans });
  const controlsQ = useQuery({ queryKey: ["usage-app-controls"], queryFn: usageAppControls });
  const rows = overviewQ.data || [];
  const plans = plansQ.data || [];

  const exportCsv = async () => {
    try {
      const blob = await usageExportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vozelto-usage.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["usage-admin-overview"] });
    qc.invalidateQueries({ queryKey: ["usage-audit"] });
    setManage(null);
  };

  const totals = rows.reduce((a, r) => ({
    credits: a.credits + (r.credits || 0), cost: a.cost + (r.est_cost || 0),
    revenue: a.revenue + (r.est_revenue || 0), blocked: a.blocked + (r.blocked_requests || 0),
  }), { credits: 0, cost: 0, revenue: 0, blocked: 0 });

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Super Admin"
        title="Usage Control"
        subtitle="Monitor AI usage, costs and trials across every workspace — with emergency platform-wide controls."
      >
        <Button variant="outline" className="border-white/15" onClick={exportCsv} data-testid="usage-export-csv">
          <DownloadSimple size={16} className="mr-1.5" /> Export CSV
        </Button>
      </PageHeader>

      <div className="space-y-5">
        {controlsQ.data && controlsQ.data.email_configured === false && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex items-center gap-2" data-testid="email-warning-banner">
            <Warning size={17} className="shrink-0 text-amber-400" />
            Transactional billing emails are not active. Add <code className="text-amber-100">RESEND_API_KEY</code> and verify the sending domain to enable delivery.
          </div>
        )}
        <AppControls />

        <Tabs defaultValue="workspaces">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="workspaces" data-testid="tab-workspaces">Workspaces</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Plan limits</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">Billing emails</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit trail</TabsTrigger>
          </TabsList>

          <TabsContent value="workspaces" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Credits used", totals.credits.toFixed(0)], ["Est. AI cost", `$${totals.cost.toFixed(2)}`],
                ["Est. revenue", `$${totals.revenue.toFixed(0)}`], ["Blocked requests", totals.blocked]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                  <div className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">{l}</div>
                  <div className="text-xl font-semibold mt-0.5">{v}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Input placeholder="Search workspaces…" value={search} onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-56" data-testid="usage-search-input" />
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="h-9 w-48 bg-white/5 border-white/10" data-testid="usage-plan-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white">
                  <SelectItem value="all">All plans</SelectItem>
                  {plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-44 bg-white/5 border-white/10" data-testid="usage-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161c] border-white/10 text-white">
                  <SelectItem value="all">All statuses</SelectItem>
                  {["trialing", "active", "expired", "past_due", "suspended"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-xl border border-white/10 overflow-x-auto" data-testid="usage-workspace-table">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-[#9a9a9a]">
                  <tr>
                    <th className="p-3">Workspace</th><th className="p-3">Plan</th><th className="p-3">Status</th>
                    <th className="p-3">Trial</th><th className="p-3">Credits</th><th className="p-3">Cost</th>
                    <th className="p-3">Rev</th><th className="p-3">Margin</th><th className="p-3">Blocked</th>
                    <th className="p-3">Flags</th><th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.client_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`usage-row-${r.client_id}`}>
                      <td className="p-3 font-medium">{r.name}</td>
                      <td className="p-3 text-xs">{r.plan_name}</td>
                      <td className={`p-3 text-xs font-medium ${STATUS_COLORS[r.billing_status] || ""}`}>{r.billing_status}{r.ai_suspended && <span className="text-red-400 ml-1">· AI off</span>}</td>
                      <td className="p-3 text-xs">
                        {r.trial_days_left != null ? (
                          <span className={r.trial_days_left <= 3 ? "text-red-400" : r.trial_days_left <= 7 ? "text-amber-300" : ""}>{r.trial_days_left}d left</span>
                        ) : <span className="text-[#9a9a9a]">—</span>}
                      </td>
                      <td className="p-3">{r.credits}</td>
                      <td className="p-3 text-xs">${r.est_cost}</td>
                      <td className="p-3 text-xs">${r.est_revenue}</td>
                      <td className={`p-3 text-xs ${r.est_margin < 0 ? "text-red-400" : "text-emerald-400"}`}>${r.est_margin}</td>
                      <td className="p-3 text-xs">{r.blocked_requests || 0}</td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          {r.high_usage && <Badge className="bg-amber-500/20 text-amber-300 border-0 text-[10px]">high usage</Badge>}
                          {(r.approaching_limits || []).slice(0, 2).map((f) => (
                            <Badge key={f} variant="outline" className="border-amber-500/40 text-amber-300 text-[10px]"><Warning size={10} className="mr-0.5" />{f.replace(/_/g, " ")}</Badge>
                          ))}
                          {r.beta_status && <Badge variant="outline" className="border-white/15 text-[#9a9a9a] text-[10px]">beta: {r.beta_status}</Badge>}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" className="border-white/15 h-7 text-xs" data-testid={`usage-manage-${r.client_id}`}
                          onClick={() => setManage(r)}>Manage</Button>
                      </td>
                    </tr>
                  ))}
                  {!rows.length && !overviewQ.isLoading && (
                    <tr><td className="p-4 text-sm text-[#9a9a9a]" colSpan={11}>No workspaces match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="plans" className="mt-4"><PlansEditor /></TabsContent>
          <TabsContent value="emails" className="mt-4"><BillingEmailsPanel /></TabsContent>
          <TabsContent value="audit" className="mt-4"><AuditTrail /></TabsContent>
        </Tabs>
      </div>

      {manage && <ManageDialog row={manage} plans={plans} onClose={() => setManage(null)} onSaved={refresh} />}
    </PageContainer>
  );
}
